import pencil from '@ingka/ssr-icon/paths/pencil';
import { Button, FlexPanel, H2 } from 'Common';
import DynamicFormContainerModal from 'ExtendedForm/DynamicForm/DynamicFormContainerModal';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { setNewPassword } from 'Redux/auth/authThunks';
import { clearFormAction } from 'Redux/forms/formsActions';
import {
  updateCustomerPersonalInfo,
  updateProfile,
  updateProfileSettings,
  updateUserLogin
} from 'Redux/profile/profileThunks';
import { LOADING } from 'Redux/userInterface/userInterfaceConstants';
import { t } from 'Utils/localization/i18next';
import { trimAllObjPropertiesSkipPasswords } from 'Utils/object/objectUtils';
import { showToast } from 'Utils/toastUtils';
import { isEditModeEnabled } from 'Utils/url/urlUtils';
import styles from './SectionReadContainer.module.scss';

class PersonalInformationSectionReadContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isEditing: isEditModeEnabled(window.location.search, 'personal')
    };

    this.myRef = React.createRef();
    this.updateActions = {
      personal: this.props.updateCustomerPersonalInfo,
      contact: this.props.updateUserLogin,
      credentials: this.props.setNewPassword,
      communication: this.props.updateProfileSettings
    };
  }

  onEdit = () => {
    this.setState({ isEditing: true });
  };

  onClose = () => {
    this.setState({ isEditing: false }, () => {
      this.props.clearFormAction(this.props.editForm);
      this.scrollToCenter();
    });
  };

  showUpdateToast = (hasMemberPanelChanged, isMemberPanelActive) => {
    const OPT_IN = 'communicationMemberPanelOptIn';
    const OPT_OUT = 'communicationMemberPanelOptOut';

    const messages = {
      personal: {
        title: 'dashboard.confirmation.personalSectionUpdateTitle',
        text: 'dashboard.confirmation.personalSectionUpdateText'
      },
      contact: {
        title: 'dashboard.confirmation.contactSectionUpdateTitle',
        text: 'dashboard.confirmation.contactSectionUpdateText'
      },
      credentials: {
        title: 'dashboard.confirmation.passwordSectionUpdateTitle',
        text: 'dashboard.confirmation.passwordSectionUpdateText'
      },
      communication: {
        title: 'dashboard.confirmation.communicationSectionUpdateTitle',
        text: 'dashboard.confirmation.communicationSectionUpdateText'
      },
      // NOTE: This component is always configured with type='personal'.
      // The member panel field only appears in communication/settings forms
      // (DashboardSectionReadContainer). These branches never execute here.
      [OPT_IN]: {
        title: 'dashboard.confirmation.communicationMemberPanelOptInTitle',
        text: 'dashboard.confirmation.communicationMemberPanelOptInText'
      },
      [OPT_OUT]: {
        title: 'dashboard.confirmation.communicationMemberPanelOptOutTitle',
        text: 'dashboard.confirmation.communicationMemberPanelOptOutText'
      }
    };

    let msgKey = this.props.type;
    if (hasMemberPanelChanged) {
      msgKey = isMemberPanelActive ? OPT_IN : OPT_OUT;
    }
    const msg = messages[msgKey];
    if (msg) {
      showToast(t(msg.title), t(msg.text));
    }
  };

  onSubmit = async data => {
    const updateAction =
      this.updateActions[this.props.type] || this.props.updateProfile;

    const memberPanelOldState = this.props.profile.participateInMemberPanel;

    const newProfile = { ...this.props.profile, ...data };
    const success = await updateAction(
      trimAllObjPropertiesSkipPasswords(newProfile),
      this.props.editForm,
      null
    );

    const isMemberPanelActive = newProfile.participateInMemberPanel;
    const hasMemberPanelChanged = memberPanelOldState !== isMemberPanelActive;

    if (success) {
      this.showUpdateToast(hasMemberPanelChanged, isMemberPanelActive);
      this.setState({ isEditing: false });
    }
    this.scrollToCenter();
  };

  scrollToCenter = () => {
    if (this.myRef) {
      this.myRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  setRef = node => {
    if (node) {
      this.myRef = node;
    }
  };

  render() {
    return (
      <>
        <div ref={this.setRef} className={styles['dashboard-section-read']}>
          <FlexPanel spread className={styles['dashboard-section-read-title']}>
            {this.props.title && (
              <H2 tagName="h2" headingSize="m" margin={false}>
                {t(this.props.title)}
              </H2>
            )}
            {this.props.editForm && (
              <>
                <Button
                  className={styles['edit-link']}
                  size="small"
                  type="tertiary"
                  id={`${this.props.editForm}-link`}
                  onClick={this.onEdit}
                  ssrIcon={pencil}
                >
                  {t('dashboard.account.edit.personalInformation')}
                </Button>
                <Button
                  className={styles['edit-link-mobile']}
                  size="small"
                  type="tertiary"
                  id={`${this.props.editForm}-link-mobile`}
                  onClick={this.onEdit}
                  aria-label={t('dashboard.account.edit.personalInformation')}
                  iconOnly
                  ssrIcon={pencil}
                ></Button>
              </>
            )}
          </FlexPanel>
          {this.state.isEditing && (
            <DynamicFormContainerModal
              formId={this.props.editForm}
              prefillValues={this.props.profile}
              loadingSubmit={this.props.isLoading}
              textSubmit={t('common.save')}
              textCancel={t('common.cancel')}
              onCancel={this.onClose}
              onSubmit={this.onSubmit}
              title={this.props.title}
            />
          )}
          {this.props.children}
        </div>
      </>
    );
  }
}

PersonalInformationSectionReadContainer.propTypes = {
  title: PropTypes.string,
  editForm: PropTypes.string,
  type: PropTypes.oneOf([
    'personal',
    'contact',
    'credentials',
    'communication'
  ]),
  isLastElement: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node)
  ]),
  // From Redux/Thunks
  profile: PropTypes.object,
  userLogin: PropTypes.string,
  isLoading: PropTypes.bool,
  updateProfile: PropTypes.func.isRequired,
  updateCustomerPersonalInfo: PropTypes.func.isRequired,
  updateUserLogin: PropTypes.func.isRequired,
  updateProfileSettings: PropTypes.func.isRequired,
  setNewPassword: PropTypes.func.isRequired,
  clearFormAction: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  return {
    profile: state.profile,
    userLogin: state.auth.userLogin,
    isLoading: state.userInterface.loading[LOADING.PROFILE_UPDATE]
  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      updateProfile,
      updateUserLogin,
      updateProfileSettings,
      updateCustomerPersonalInfo,
      setNewPassword,
      clearFormAction
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PersonalInformationSectionReadContainer);
